import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { HiUsers } from 'react-icons/hi' // Add this with other imports
import useTitle from '../../hooks/useTitle'
import { Checkbox } from '../../components/ui/checkbox'
import { Button } from '../../components/ui/button'
import NotFound from '../not-found'
import axiosInstance from '../../api/axiosInstance'
import DataTableShimmer from '../../components/data-table/DataTableShimmer'
import { AuthContext } from '../../contexts/auth-context'
import { useContext } from 'react'
import { FiEdit, FiTrash2 } from 'react-icons/fi'
import { getStaticFilesSrc } from '@/api/getStaticFilesSrc'
import { generateCardHTML } from '../../config/cardGenerator'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { useNavigate } from 'react-router-dom'
import { useDeviceSize } from '../../hooks/useDeviceSize'

dayjs.extend(relativeTime)

function MyCards() {
  const { auth } = useContext(AuthContext)
  const businessId = auth.user._id
  const [cards, setCards] = useState([])
  useTitle('My Cards')
  const navigate = useNavigate()
  const isLargeDevice = useDeviceSize()
  const [showDeviceWarning, setShowDeviceWarning] = useState(false)

  const [deleteConfirmation, setDeleteConfirmation] = useState(false)
  const [cardToDelete, setCardToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const [activeConfirmation, setActiveConfirmation] = useState(false)
  const [cardToToggle, setCardToToggle] = useState(null)
  const [newActiveState, setNewActiveState] = useState(false)

  // Fetch cards data
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['loyaltyCards', businessId],
    queryFn: async () => {
      let data = {}
      try {
        const response = await axiosInstance.get('web-api/getCards', {
          params: { businessId },
        })
        data = response.data
      } catch (error) {
        data = {
          cards: [],
          total: 0,
        }
      }

      return data
    },
  })

  useEffect(() => {
    if (data?.data?.cards) {
      setCards(data.data.cards)
    }
  }, [data])

  // Update card status
  const statusMutation = useMutation({
    mutationFn: ({ cardId, isActive }) =>
      axiosInstance.post('web-api/updateCardStatus', { cardId, isActive }),
    onSuccess: () => {
      toast.success('Card status updated')
      refetch()
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Error updating card status')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (cardId) => axiosInstance.post('web-api/deleteCard', { cardId }),
    onSuccess: (_, cardId) => {
      setCards((prevCards) => prevCards.filter((card) => card._id !== cardId))
      toast.success('Card deleted successfully')
      refetch()
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Error deleting card')
    },
  })

  const handleStatusChange = (card, isActive) => {
    // Prevent toggling if card is already active
    if (card.isActive && isActive) {
      toast.info('This card is already active')
      return
    }

    setCardToToggle(card._id)
    setNewActiveState(isActive)
    setActiveConfirmation(true)
  }

  const handleConfirmStatusChange = async () => {
    try {
      await statusMutation.mutateAsync({
        cardId: cardToToggle,
        isActive: newActiveState,
      })
      setActiveConfirmation(false)
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error updating card status')
    }
  }

  const handleDeleteClick = (cardId) => {
    setCardToDelete(cardId)
    setDeleteConfirmation(true)
  }

  const handleConfirmDelete = async () => {
    setIsDeleting(true)
    try {
      await deleteMutation.mutateAsync(cardToDelete)
      setDeleteConfirmation(false)
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error deleting card')
    } finally {
      setIsDeleting(false)
    }
  }

  const getTemplateById = (id) => {
    return cardTemplates.find((template) => template.id === id) || cardTemplates[0]
  }

  const handleAddNewCard = () => {
    if (!isLargeDevice) {
      setShowDeviceWarning(true)
      return
    }
    navigate('/business/create-card')
  }

  const handleEditCard = (card) => {
    if (!isLargeDevice) {
      setShowDeviceWarning(true)
      return
    }
    navigate('/business/edit-card', {
      state: { card: card?.card, cardId: card?._id },
    })
  }

  if (isLoading) return <DataTableShimmer columns={6} />
  if (error) return <NotFound fullScreen={false} />

  return (
    <div className="container mx-auto p-4">
      <div className="flex flex-row justify-between w-full">
        <h1 className="text-2xl font-bold mb-6">My Loyalty Cards</h1>
        <Button
          onClick={handleAddNewCard}
          className="bg-theme-color-800 cursor-pointer hover:text-theme-color-800 hover:bg-white hover:border hover:border-theme-color-700 shadow-2xl border"
        >
          Add New Card
        </Button>
      </div>

      {cards.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">You haven't created any loyalty card yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {cards?.map((card) => (
            <div key={card._id} className="flex flex-col items-center w-full justify-center">
              <div className="relative w-[300px] h-[160px] md:w-[490px] md:h-[180px] lg:w-[490px] lg:h-[245px] flex items-center  justify-center">
                <div
                  className="transform scale-[0.46] md:scale-[0.5] lg:scale-[0.7]"
                  style={{
                    width: '700px',
                    height: '350px',
                  }}
                  dangerouslySetInnerHTML={{ __html: generateCardHTML(card.card) }}
                />
              </div>

              <div className="w-[300px] md:w-[360px] lg:w-[490px]  m-0 flex justify-between ps-2">
                <div className={`flex items-center gap-0`}>
                  <Checkbox
                    id={`active-${card._id}`}
                    checked={card.isActive}
                    onCheckedChange={(checked) => handleStatusChange(card, checked)}
                    disabled={statusMutation.isLoading || card.isActive}
                    className={`bg-white ${!card.isActive ? 'cursor-pointer' : ''} ${card.isActive ? 'opacity-50' : ''}`}
                  />
                  <label
                    htmlFor={`active-${card._id}`}
                    className={`text-sm font-medium ps-2 text-gray-600 flex items-center gap-2 ${!card.isActive ? 'cursor-pointer' : ''}`}
                  >
                    Active
                    <div
                      className={`h-2 w-2 rounded-full mt-[1px] ${!card.isActive ? 'cursor-pointer' : ''} ${
                        card.isActive ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    />
                  </label>
                </div>

                <div className="flex items-center">
                  {card.customerCount > 0 ? (
                    <div className="flex items-center gap-2 bg-purple-50 px-3 mt-0.5 rounded-full">
                      <HiUsers className="w-4 h-4 text-purple-600" /> customers :
                      <span className="text-sm font-semibold text-purple-600">
                        {card.customerCount}
                      </span>
                    </div>
                  ) : (
                    <div className="flex">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditCard(card)}
                        className="text-blue-500 cursor-pointer"
                      >
                        <FiEdit className="h-2 w-2 text-blue-500" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClick(card._id)}
                        disabled={deleteMutation.isLoading}
                        className="text-red-500 cursor-pointer"
                      >
                        <FiTrash2 className="h-2 w-2 text-red-500" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={deleteConfirmation} onOpenChange={setDeleteConfirmation}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Are you sure?</DialogTitle>
            <DialogDescription>
              This action cannot be undone. The card will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmation(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete} disabled={isDeleting}>
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeviceWarning} onOpenChange={setShowDeviceWarning}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Desktop Required</DialogTitle>
            <DialogDescription>
              Card creation and editing features are only available on desktop or larger screen
              devices. Please switch to a desktop device for the best experience with our card
              editor.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="primary" onClick={() => setShowDeviceWarning(false)}>
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={activeConfirmation} onOpenChange={setActiveConfirmation}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{newActiveState ? 'Activate Card?' : 'Deactivate Card?'}</DialogTitle>
            <DialogDescription>
              {newActiveState
                ? 'This will make this card active and deactivate all other cards.'
                : 'Are you sure you want to deactivate this card?'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setActiveConfirmation(false)}
              disabled={statusMutation.isLoading}
            >
              Cancel
            </Button>
            <Button
              variant={newActiveState ? 'default' : 'destructive'}
              onClick={handleConfirmStatusChange}
              disabled={statusMutation.isLoading}
              className="bg-[#7B4B87] hover:bg-[#693E74] text-white font-semibold shadow-md"
            >
              {statusMutation.isLoading
                ? 'Updating...'
                : newActiveState
                  ? 'Yes, Activate'
                  : 'Yes, Deactivate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default MyCards
